import React from "react";
import { cn } from "../lib/utils";
import { X } from "./icons";
import { Source } from "../lib/sources-utils";

interface SourcesPanelProps {
  sources: Source[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  highlightedSourceId?: string;
  onSourceClick?: (source: Source) => void;
}

export const SourcesPanel: React.FC<SourcesPanelProps> = ({
  sources,
  isOpen,
  onToggle,
  onClose,
  highlightedSourceId,
  onSourceClick,
}) => {
  const handleSourceClick = (source: Source) => {
    // Call custom handler if provided, otherwise default to opening URL
    if (onSourceClick) {
      onSourceClick(source);
    } else {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={onClose}
        />
      )}

      {/* Sources Panel */}
      <aside
        className={cn(
          "fixed right-0 top-0 h-full w-80 bg-zinc-900/95 backdrop-blur-sm border-l border-zinc-700/50 z-50 transition-transform duration-300 ease-in-out",
          "md:relative md:z-auto md:bg-zinc-900 md:backdrop-blur-none",
          isOpen ? "translate-x-0" : "translate-x-full md:w-0 md:h-0 md:overflow-hidden"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700/50">
          <h2 className="text-lg font-semibold text-white">Sources</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-zinc-700/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sources List */}
        <div className="flex-1 overflow-y-auto p-4">
          {sources.length === 0 ? (
            <div className="text-zinc-400 text-sm text-center py-8">
              No sources available
            </div>
          ) : (
            <div className="space-y-3">
              {sources.map((source) => (
                <div
                  key={source.id}
                  onClick={() => handleSourceClick(source)}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors group",
                    highlightedSourceId === source.id 
                      ? "bg-emerald-500/20 border border-emerald-500/30" 
                      : "bg-zinc-800/50 hover:bg-zinc-700/50"
                  )}
                >
                  {/* Numbered Icon */}
                  <div className={cn(
                    "flex-shrink-0 w-6 h-6 text-white text-xs font-medium rounded-full flex items-center justify-center",
                    highlightedSourceId === source.id 
                      ? "bg-emerald-400" 
                      : "bg-emerald-500"
                  )}>
                    {source.number}
                  </div>
                  
                  {/* Source Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium line-clamp-2 group-hover:text-emerald-400 transition-colors">
                      {source.title}
                    </div>
                    <div className="text-zinc-400 text-xs mt-1 truncate">
                      {source.url}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
